import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  BulkPromote,
  ErrorResponse,
  FetchEvictCache,
  FetchUpdateModelShowroomState,
  FetchUpdateTrimShowroomState,
  FlushImages,
  LocaleYearMakeData,
  LocalizedVehicleData,
  ModelBodyStyleData,
  ModelCreateRequest,
  ModelCreateResponse,
  SearchResponse,
  StageType,
  Trim,
  TrimCreateRequest,
  TrimCreateResponse,
  TrimMap,
  TrimMapWithDetails,
  TrimShowroomViewFormData,
  TrimViewFormData,
  TrimWithDetails,
  VehicleMap,
  VehicleSearchFilters,
  VehicleSearchResponse,
  VehicleViewFormData,
  VehicleWithDetails
} from "@dealer-center/inventory-pages-gvm-interface";
import { useToaster } from "@dealer-center/core-components";
import {
  defaultSearchInput,
  getLiveQueryFilters,
  saveLiveQueryFilters,
  toQueryParams,
  toSearchFilters,
  trimMetadata
} from "@dealer-center/inventory-core-utils";
import type { SetStateAction } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const baseURL = "/inventory/nxlambda/gvm";
const queryKeyBase = "inventory-pages-gvm";

const invalidateErrorMessage = "Error invalidating queries";

export function useGetLocalizedVehicleData(): SearchResponse<LocalizedVehicleData> {
  const { data, error, isLoading, isError, isFetching } = useQuery({
    queryKey: [queryKeyBase + "-localized-vehicle-data"],
    queryFn: () => fetchLocalizedVehicleData(),
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  return {
    data,
    isLoading,
    isFetching,
    error: isError ? error : null
  };
}

const fetchLocalizedVehicleData = async (): Promise<LocalizedVehicleData> => {
  const res = await fetch(`${baseURL}/fullFormData`);
  if (res.ok) {
    return res.json();
  } else {
    throw await backendError(res);
  }
};

export function useVehicleSearch() {
  const [searchParams] = useSearchParams();
  const initFilters = () => {
    if (searchParams.size > 0) {
      return toSearchFilters(searchParams);
    } else {
      const filters = getLiveQueryFilters();
      return filters ?? defaultSearchInput;
    }
  };
  const [searchFilters, setSearchFilters] =
    useState<VehicleSearchFilters>(initFilters);
  const [searchFiltersCopy, setSearchFiltersCopy] =
    useState<VehicleSearchFilters>(initFilters);

  const syncSearchFilters = (updater: SetStateAction<VehicleSearchFilters>) => {
    setSearchFilters(updater);
    setSearchFiltersCopy(updater);
  };

  useEffect(() => {
    if (searchParams.size > 0) {
      // Sync filters to localstorage
      saveLiveQueryFilters(toSearchFilters(searchParams));
    }
  }, []);

  return {
    searchFilters,
    setSearchFilters,
    searchFiltersCopy,
    setSearchFiltersCopy,
    syncSearchFilters
  };
}

export function useNavigateToLiveQueryPage(): () => void {
  const navigate = useNavigate();
  const filters = getLiveQueryFilters();

  if (!filters) return () => navigate("/gvm");

  const params = toQueryParams(filters);

  return () =>
    navigate(
      { pathname: "/gvm", search: params.toString() },
      { replace: true }
    );
}

export function useGetVehicleSearch({
  stage,
  locale,
  year,
  make,
  model,
  max,
  offset,
  page
}: VehicleSearchFilters): SearchResponse<VehicleSearchResponse> {
  const { data, error, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      queryKeyBase,
      "vehicle-search",
      "search-by-filters",
      { stage, locale, year, make, model, max, offset, page }
    ],
    queryFn: () =>
      fetchVehicleSearch(
        stage,
        locale,
        year ?? 0,
        make ?? "",
        model ?? "",
        max,
        offset
      ),
    enabled:
      !!stage &&
      stage.trim().length > 0 &&
      !!locale &&
      locale.trim().length > 0 &&
      (stage === "QA" || !!year),
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  return {
    data,
    isLoading,
    isFetching,
    error: isError ? error : null
  };
}

/** ${apiUrl}/gvm/vehicle/search?stage=LIVE&locale=US_EN&year=2024&make=honda&model=Civic%20Type%20R&max=10&offset=0 */
const fetchVehicleSearch = async (
  stage: StageType,
  locale: string,
  year: number,
  make: string,
  model: string,
  max: number,
  offset: number
): Promise<VehicleSearchResponse> => {
  const params = new URLSearchParams({
    stage,
    locale,
    year: String(year),
    make,
    model,
    max: String(max),
    offset: String(offset)
  });
  const url = `${baseURL}/vehicleSearch?${params.toString()}`;
  const res = await fetch(url);
  if (res.ok) {
    return res.json();
  } else {
    throw await backendError(res);
  }
};

export function useGetVehicleWithDetails({
  locale,
  vehicleId
}: VehicleViewFormData): SearchResponse<VehicleWithDetails> {
  const { data, error, isLoading, isError, isFetching } = useQuery({
    queryKey: [
      queryKeyBase,
      "vehicle-search",
      "search-by-id-locale",
      { locale, vehicleId }
    ],
    queryFn: () => fetchVehicleEditWithDetails(locale ?? "", vehicleId ?? ""),
    enabled:
      !!locale &&
      locale.trim().length > 0 &&
      !!vehicleId &&
      vehicleId.trim().length > 0,
    retry: 1
  });

  return {
    data,
    isLoading,
    isFetching,
    error: isError ? error : null
  };
}

/** ${apiUrl}/gvm/vehicle/mapping?vehicleId=US2023AUD073d37cf3dd94407c538f23&locale=US_EN */
const fetchVehicleEditWithDetails = async (
  locale: string,
  vehicleId: string
): Promise<VehicleWithDetails> => {
  const url = `${baseURL}/vehicleEditMapping?locale=${locale}&vehicleId=${vehicleId}`;
  const res = await fetch(url);

  if (res.status === 204) {
    const message = `Vehicle with id ${vehicleId} and locale ${locale} not found`;
    throw new Error(message);
  } else if (res.ok) {
    return res.json();
  } else {
    throw await backendError(res);
  }
};

export function useGetTrimMapWithDetails({
  locale,
  trimId
}: TrimViewFormData): SearchResponse<TrimMapWithDetails> {
  const { data, error, isLoading, isError, isFetching } = useQuery({
    queryKey: [
      queryKeyBase,
      "trim-search",
      "search-by-id-locale",
      { locale, trimId }
    ],
    queryFn: () => fetchTrimEditWithDetails(locale ?? "", trimId ?? ""),
    enabled:
      !!locale &&
      locale.trim().length > 0 &&
      !!trimId &&
      trimId.trim().length > 0,
    retry: 1
  });

  return {
    data,
    isLoading,
    isFetching,
    error: isError ? error : null
  };
}

/** ${apiUrl}/gvm/trim/mapping?trimId=US2023AUD073d37cf3dd94407c538f23&locale=US_EN */
const fetchTrimEditWithDetails = async (
  locale: string,
  trimId: string
): Promise<TrimMapWithDetails> => {
  const url = `${baseURL}/trimEditMapping?locale=${locale}&trimId=${trimId}`;
  const res = await fetch(url);

  if (res.status === 204) {
    const message = `Trim with id ${trimId} and locale ${locale} not found`;
    throw new Error(message);
  } else if (res.ok) {
    return res.json();
  } else {
    throw await backendError(res);
  }
};

export function useGetLocaleYearMake(): SearchResponse<LocaleYearMakeData> {
  const { data, error, isLoading, isError, isFetching } = useQuery({
    queryKey: [queryKeyBase, "locale-year-make"],
    queryFn: () => fetchLocaleYearMakeList(),
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  return {
    data,
    isLoading,
    isFetching,
    error: isError ? error : null
  };
}

const fetchLocaleYearMakeList = async (): Promise<LocaleYearMakeData> => {
  const res = await fetch(`${baseURL}/modelCreatorFormData`);
  if (res.ok) {
    return res.json();
  } else {
    throw await backendError(res);
  }
};

export function useGetModelBodyStyle({
  stage,
  locale,
  year,
  make,
  model
}: TrimCreateRequest): SearchResponse<ModelBodyStyleData> {
  const { data, error, isLoading, isError, isFetching } = useQuery({
    queryKey: [
      queryKeyBase,
      "model-bodystyle",
      { stage, locale, year, make, model }
    ],
    queryFn: () =>
      fetchModelBodyStyleList(
        locale ?? "US_EN",
        year ?? 0,
        make ?? "",
        stage,
        model ?? ""
      ),
    enabled:
      !!stage &&
      stage.trim().length > 0 &&
      !!locale &&
      locale.trim().length > 0 &&
      !!year &&
      !!make &&
      make.trim().length > 0,
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  return {
    data,
    isLoading,
    isFetching,
    error: isError ? error : null
  };
}

const fetchModelBodyStyleList = async (
  locale: string,
  year: number,
  make: string,
  stage: StageType,
  model: string
): Promise<ModelBodyStyleData> => {
  const params = new URLSearchParams({
    stage,
    locale,
    year: String(year),
    make,
    model
  });
  const url = `${baseURL}/trimCreatorBodyStyleFormData?${params.toString()}`;
  const res = await fetch(url);
  if (res.ok) {
    return res.json();
  } else {
    throw await backendError(res);
  }
};

export function useModelCreate() {
  const { toastError, toastSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase + "-localized-vehicle-data"]
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase, "vehicle-search", "search-by-filters"]
      });
      toastSuccess("Created", "Model created successfully");
    },
    onError: (error: Error) => {
      toastError(error.message);
    },
    mutationFn: fetchModelCreate
  });
}

async function fetchModelCreate(
  modelCreateRequest: ModelCreateRequest
): Promise<ModelCreateResponse> {
  const res = await fetch(`${baseURL}/modelTrimCreator/createmodel`, {
    method: "POST",
    body: JSON.stringify(modelCreateRequest)
  });

  if (res.ok) {
    return res.json();
  } else {
    throw await backendError(res);
  }
}

export function useGetTrimToCloneList({
  stage,
  locale,
  year,
  make,
  model,
  bodystyle
}: TrimCreateRequest): SearchResponse<Trim[]> {
  const { data, error, isLoading, isError, isFetching } = useQuery({
    queryKey: [
      queryKeyBase,
      "trim-for-clone",
      { stage, locale, year, make, model, bodystyle }
    ],
    queryFn: () =>
      fetchTrimToCloneList(
        locale ?? "US_EN",
        year ?? 0,
        make ?? "",
        stage,
        model ?? "",
        bodystyle ?? ""
      ),
    enabled:
      !!stage &&
      stage.trim().length > 0 &&
      !!locale &&
      locale.trim().length > 0 &&
      !!year &&
      !!make &&
      make.trim().length > 0 &&
      !!model &&
      model.trim().length > 0 &&
      !!bodystyle &&
      bodystyle.trim().length > 0,
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
  return {
    data,
    isLoading,
    isFetching,
    error: isError ? error : null
  };
}

const fetchTrimToCloneList = async (
  locale: string,
  year: number,
  make: string,
  stage: StageType,
  model: string,
  bodystyle: string
): Promise<Trim[]> => {
  const params = new URLSearchParams({
    stage,
    locale,
    year: String(year),
    make,
    model,
    bodyStyle: bodystyle
  });
  const url = `${baseURL}/trimCreatorUniqueTrimFormData?${params.toString()}`;
  const res = await fetch(url);
  if (res.ok) {
    return res.json();
  } else {
    throw await backendError(res);
  }
};

export function useGetTrimWithDetails({
  locale,
  trimId
}: TrimCreateRequest): SearchResponse<TrimWithDetails> {
  const { data, error, isLoading, isError, isFetching } = useQuery({
    queryKey: [queryKeyBase, "trim-with-details", { locale, trimId }],
    queryFn: () => fetchTrimWithDetails(locale || "US_EN", trimId || ""),
    enabled:
      !!locale &&
      locale.trim().length > 0 &&
      !!trimId &&
      trimId.trim().length > 0,
    retry: 1,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
  return {
    data,
    isLoading,
    isFetching,
    error: isError ? error : null
  };
}

const fetchTrimWithDetails = async (
  locale: string,
  trimId: string
): Promise<TrimWithDetails> => {
  const url = `${baseURL}/trimMappingTrimFormData?locale=${locale}&trimId=${trimId}`;
  const res = await fetch(url);
  if (res.ok) {
    return res.json();
  } else {
    throw await backendError(res);
  }
};

export function useTrimCreate() {
  const { toastError, toastSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase, "vehicle-search", "search-by-filters"]
      });
      toastSuccess("Created", "Trim created successfully");
    },
    onError: (error: Error) => {
      console.log("Use ERROR", error);
      toastError(error.message);
    },
    mutationFn: fetchTrimCreate
  });
}

async function fetchTrimCreate(
  trimCreateRequest: TrimCreateRequest
): Promise<TrimCreateResponse> {
  const request = {
    trimYear: trimCreateRequest.year,
    trimMake: trimCreateRequest.make,
    trimModel: trimCreateRequest.model,
    trimBodyStyle: trimCreateRequest.bodystyle,
    globalVehicleTrimId: trimCreateRequest.trimId,
    trimDescription: trimCreateRequest.uniqueTrim,
    trimLocale: trimCreateRequest.locale,
    trimMapsToClone: trimCreateRequest.mappings
  };
  const response = await fetch(`${baseURL}/modelTrimCreator/createTrim`, {
    method: "POST",
    body: JSON.stringify(request)
  });

  if (response.ok) {
    return response.json();
  } else {
    let err: Error;
    try {
      const data = await response.json();
      err = new Error(data.errorMessage);
    } catch (error) {
      err = new Error("Unexpected error occurred");
    }
    throw err;
  }
}

export function useVehicleSave() {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [queryKeyBase, "vehicle-search"]
        })
        .catch(() => toastError(invalidateErrorMessage));
      toastSuccess("Updated", "Vehicle was updated successfully");
    },
    onError: () => {
      toastError("Error updating the vehicle");
    },
    mutationFn: fetchVehicleSave
  });
}

async function fetchVehicleSave(vehicle: Record<string, string>) {
  const prefixedBody: Record<string, string> = {
    locale: vehicle.locale,
    vehicleId: vehicle.vehicleId
  };

  for (const key in vehicle) {
    if (key === "locale" || key === "vehicleId") continue;
    prefixedBody["p_" + key] = vehicle[key];
  }

  const response = await fetch(`${baseURL}/vehicle`, {
    method: "POST",
    headers: {
      "vehicle-id": vehicle.vehicleId || ""
    },
    body: JSON.stringify(prefixedBody)
  });

  if (!response.ok) {
    throw await backendError(response);
  }
}

export function useShowroomStateUpdate(toInclude: boolean) {
  const mutationFn = toInclude
    ? fetchIncludeModelShowroom
    : fetchExcludeModelShowroom;
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [queryKeyBase, "vehicle-search"]
        })
        .catch(() => toastError(invalidateErrorMessage));
      toastSuccess("Updated", "Showroom state updated");
    },
    onError: () => {
      toastError("Error updating showroom state");
    },
    mutationFn
  });
}

async function fetchIncludeModelShowroom({
  vehicleId,
  make,
  locale
}: FetchUpdateModelShowroomState): Promise<void> {
  const params = new URLSearchParams({
    vehicleId,
    make,
    locale
  });
  const response = await fetch(
    `${baseURL}/includeModelShowroom?${params.toString()}`,
    {
      method: "POST",
      headers: {
        "vehicle-id": vehicleId || ""
      }
    }
  );

  if (!response.ok) {
    throw await backendError(response);
  }
}

async function fetchExcludeModelShowroom({
  vehicleId,
  make,
  locale
}: FetchUpdateModelShowroomState): Promise<void> {
  const params = new URLSearchParams({
    vehicleId,
    make,
    locale
  });
  const response = await fetch(
    `${baseURL}/excludeModelShowroom?${params.toString()}`,
    {
      method: "POST",
      headers: {
        "vehicle-id": vehicleId || ""
      }
    }
  );

  if (!response.ok) {
    throw await backendError(response);
  }
}

export function useTrimShowroomStateUpdate({
  locale,
  trimId,
  toInclude
}: TrimShowroomViewFormData) {
  const mutationFn = toInclude
    ? fetchIncludeTrimShowroom
    : fetchExcludeTrimShowroom;
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [
            queryKeyBase,
            "trim-search",
            "search-by-id-locale",
            { locale, trimId }
          ]
        })
        .catch(() => toastError(invalidateErrorMessage));
      toastSuccess("Updated", "Showroom state updated");
    },
    onError: () => {
      toastError("Error updating showroom state");
    },
    mutationFn
  });
}

async function fetchIncludeTrimShowroom({
  vehicleId,
  trimId,
  make,
  locale
}: FetchUpdateTrimShowroomState): Promise<void> {
  const params = new URLSearchParams({
    trimId,
    make,
    locale
  });
  const response = await fetch(
    `${baseURL}/includeTrimShowroom?${params.toString()}`,
    {
      method: "POST",
      headers: {
        "vehicle-id": vehicleId || "",
        "trim-id": trimId || ""
      }
    }
  );

  if (!response.ok) {
    throw await backendError(response);
  }
}

async function fetchExcludeTrimShowroom({
  vehicleId,
  trimId,
  make,
  locale
}: FetchUpdateTrimShowroomState): Promise<void> {
  const params = new URLSearchParams({
    trimId,
    make,
    locale
  });
  const response = await fetch(
    `${baseURL}/excludeTrimShowroom?${params.toString()}`,
    {
      method: "POST",
      headers: {
        "vehicle-id": vehicleId || "",
        "trim-id": vehicleId || ""
      }
    }
  );

  if (!response.ok) {
    throw await backendError(response);
  }
}

export function useEvictCache(resetAllSelected?: () => void) {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [queryKeyBase, "vehicle-search"]
        })
        .catch(() => toastError(invalidateErrorMessage));
      toastSuccess("Flush Caches", "Flushing will take minutes");
      if (resetAllSelected) resetAllSelected();
    },
    onError: () => {
      toastError("Error flushing caches");
    },
    mutationFn: fetchEvictCache
  });
}

async function fetchEvictCache(evictCache: FetchEvictCache): Promise<void> {
  const response = await fetch(`${baseURL}/evictCache`, {
    method: "POST",
    headers: {
      "vehicle-id": evictCache.vehicleIds.join(", "),
      "trim-id": evictCache.trimIds.join(", ")
    },
    body: JSON.stringify(evictCache)
  });

  if (!response.ok) {
    throw await backendError(response);
  }
}

export function useVehicleMapSave() {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [queryKeyBase, "vehicle-search"]
        })
        .catch(() => toastError(invalidateErrorMessage));
      toastSuccess("Updated", "Map added successfully");
    },
    onError: () => {
      toastError("Error adding the map");
    },
    mutationFn: fetchVehicleMapSave
  });
}

async function fetchVehicleMapSave(
  vehicleMap: VehicleMap
): Promise<VehicleMap> {
  const request = {
    vid: vehicleMap.globalVehicleId,
    tpid: vehicleMap.thirdPartyId,
    tpvid: vehicleMap.thirdPartyVehicleId
  };
  const response = await fetch(`${baseURL}/saveVehicleMap`, {
    method: "POST",
    headers: {
      "vehicle-id": vehicleMap.globalVehicleId || ""
    },
    body: JSON.stringify(request)
  });

  if (response.ok) {
    return response.json();
  } else {
    throw await backendError(response);
  }
}

export function useVehicleMapDelete() {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [queryKeyBase, "vehicle-search"]
        })
        .catch(() => toastError(invalidateErrorMessage));
      toastSuccess("Updated", "Map deleted successfully");
    },
    onError: () => {
      toastError("Error deleting the map");
    },
    mutationFn: fetchVehicleMapDelete
  });
}

async function fetchVehicleMapDelete(recordId: string): Promise<VehicleMap> {
  const request = {
    recordId
  };
  const response = await fetch(`${baseURL}/deleteVehicleMap`, {
    method: "POST",
    headers: {
      "vehicle-id": recordId || ""
    },
    body: JSON.stringify(request)
  });

  if (response.ok) {
    return response.json();
  } else {
    throw await backendError(response);
  }
}

export function useTrimMapSave(locale: string, trimId: string) {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [
            queryKeyBase,
            "trim-search",
            "search-by-id-locale",
            { locale, trimId }
          ]
        })
        .catch(() => toastError(invalidateErrorMessage));
      toastSuccess("Updated", "Map added successfully");
    },
    onError: () => {
      toastError("Error adding the map");
    },
    mutationFn: fetchTrimMapSave
  });
}

async function fetchTrimMapSave(trimMap: TrimMap): Promise<TrimMap> {
  const request = {
    tid: trimMap.globalVehicleTrimId,
    tpid: trimMap.thirdPartyId,
    tptid: trimMap.thirdPartyVehicleTrimId
  };
  const response = await fetch(`${baseURL}/saveTrimMap`, {
    method: "POST",
    headers: {
      "trim-id": trimMap.globalVehicleTrimId || ""
    },
    body: JSON.stringify(request)
  });

  if (response.ok) {
    return response.json();
  } else {
    throw await backendError(response);
  }
}

export function useTrimMapDelete(locale: string, trimId: string) {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [
            queryKeyBase,
            "trim-search",
            "search-by-id-locale",
            { locale, trimId }
          ]
        })
        .catch(() => toastError(invalidateErrorMessage));
      toastSuccess("Updated", "Map deleted successfully");
    },
    onError: () => {
      toastError("Error deleting the map");
    },
    mutationFn: fetchTrimMapDelete
  });
}

async function fetchTrimMapDelete(recordId: string): Promise<TrimMap> {
  const request = {
    recordId
  };
  const response = await fetch(`${baseURL}/deleteTrimMap`, {
    method: "POST",
    headers: {
      "trim-id": recordId || ""
    },
    body: JSON.stringify(request)
  });

  if (response.ok) {
    return response.json();
  } else {
    throw await backendError(response);
  }
}

export function useBulkPromote(resetAllSelected: () => void) {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [queryKeyBase, "vehicle-search"]
        })
        .catch(() => toastError(invalidateErrorMessage));
      resetAllSelected();
      toastSuccess("Updated", "Promoted successfully");
    },
    onError: () => {
      toastError("Error promoting");
    },
    mutationFn: fetchBulkPromote
  });
}

async function fetchBulkPromote(bulkPromotion: BulkPromote) {
  const response = await fetch(`${baseURL}/promoteToLive`, {
    method: "POST",
    headers: {
      "vehicle-id": bulkPromotion.vehicleIds.join(", "),
      "trim-id": bulkPromotion.trimIds.join(", ")
    },
    body: JSON.stringify(bulkPromotion)
  });

  if (!response.ok) {
    throw await backendError(response);
  }
}

export function useTrimSave() {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [queryKeyBase, "vehicle-search", "search-by-filters"]
        })
        .catch(() => toastError(invalidateErrorMessage));
      queryClient
        .invalidateQueries({
          queryKey: [queryKeyBase, "trim-search", "search-by-id-locale"]
        })
        .catch(() => toastError(invalidateErrorMessage));
      toastSuccess("Updated", "Trim was updated successfully");
    },
    onError: () => {
      toastError("Error updating the trim");
    },
    mutationFn: fetchTrimSave
  });
}

async function fetchTrimSave(trim: Record<string, string>) {
  const prefixedBody: Record<string, string> = {
    trimId: trim.trimId,
    locale: trim.locale
  };

  for (const key in trim) {
    if (key === "locale" || key === "trimId" || key === "vehicleId") continue;
    let trimmed = trim[key].trim();
    if (trimmed === "") trimmed = trimMetadata.isNumeric(key) ? "0" : "<empty>";
    prefixedBody["p_" + key] = trimmed;
  }

  const response = await fetch(`${baseURL}/trim`, {
    method: "POST",
    headers: {
      "vehicle-id": trim.vehicleId || "",
      "trim-id": trim.trimId || ""
    },
    body: JSON.stringify(prefixedBody)
  });

  if (!response.ok) {
    throw await backendError(response);
  }
}

export function useDownloadAutodataImages(locale: string, trimId: string) {
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [
            queryKeyBase,
            "trim-search",
            "search-by-id-locale",
            { locale, trimId }
          ]
        })
        .catch(() => toastError(invalidateErrorMessage));
      toastSuccess("Updated", "Redownload Images done");
    },
    onError: () => {
      toastError("Error redownloading images");
    },
    mutationFn: fetchDownloadAutodataImages
  });
}

async function fetchDownloadAutodataImages(trimId: string): Promise<void> {
  const response = await fetch(
    `${baseURL}/downloadAutodataImages?trimId=${trimId}`,
    {
      method: "POST",
      headers: {
        "trim-id": trimId || ""
      }
    }
  );

  if (!response.ok) {
    throw await backendError(response);
  }
}

export function useFlushTrimImages() {
  const { toastError, toastSuccess } = useToaster();
  return useMutation({
    onSuccess: () => {
      toastSuccess("Updated", "Flushed successfully");
    },
    onError: () => {
      toastError("Error flushing");
    },
    mutationFn: fetchFlushTrimImages
  });
}

async function fetchFlushTrimImages(flushImages: FlushImages) {
  const response = await fetch(`${baseURL}/trim/refresh`, {
    method: "POST",
    headers: {
      "trim-id": flushImages.trimId
    },
    body: JSON.stringify(flushImages)
  });

  if (!response.ok) {
    throw await backendError(response);
  }
}

async function backendError(response: Response) {
  const error: ErrorResponse = await response.json();
  return new Error(error.message);
}
